import { Box, Grid, styled }                      from '@mui/material';
import {
  FastField,
  FormikProps,
  useFormikContext,
}                                         from 'formik';
import React, {
  FC,
  RefObject,
  useMemo,
  useRef,
}                                         from 'react';
import { ReactComponent as IconDocument } from '../../../../../images/icons/icon-document.svg';
import { documentAccessLevels }           from '../../../constants/constants';
import { FamilyMembersSelector }          from '../../../formFields/FamilyMembersSelector';
import { SelectField }                    from '../../../formFields/SelectField';
import { TextInputField }                 from '../../../formFields/TextInputField';
import { WithCategorySelection }          from '../../../hocs/withCategorySelection';
import { useFetchCategories }             from '../../../queries/categories';
import { StyledDivider }                  from '../../../styledComponents';
import { DocumentPicker }                 from '../../documents/DocumentPicker';
import {
  UploadEntityFormDropdownField,
  UploadEntityFormLabel,
  UploadEntityFormRow,
  UploadEntityFormSpacing,
}                                         from '../../documents/styles';
import { GridDivider }                    from '../../layout/GridDivider';
import { ModalFooterActions }             from '../shared/ModalFooterActions';
import { IUploadDocument }                from './validation';

interface IAddDocumentModalFormProps extends FormikProps<IUploadDocument> {
  familyId?: string;
  loading: boolean;
  files: File[] | null;
  formikRef: RefObject<FormikProps<IUploadDocument> | null>;

  onChange(files: File[]): void;
}

const DescriptionField = styled(FastField)(({ theme }) => ({
  'textarea': {
    height: '0px !important',
  },
}));

export const UploadDocumentModalForm: FC<IAddDocumentModalFormProps> = ({
  familyId,
  values,
  setFieldValue,
  loading,
  files,
  formikRef,
  onChange,
}) => {
  const { data: categories, isLoading: loadingCategories } = useFetchCategories(familyId);

  // Refs keep mutable values accessible inside the stable Form closure
  // without stale-closure issues on every Formik re-render.
  const loadingRef  = useRef(loading);
  loadingRef.current = loading;

  const filesRef    = useRef(files);
  filesRef.current  = files;

  const onChangeRef    = useRef(onChange);
  onChangeRef.current  = onChange;

  // useMemo([], []) gives a stable component *type* for the lifetime of this
  // modal session.  Without this, WithCategorySelection() is called on every
  // Formik re-render (every keystroke), creating a brand-new component type
  // each time.  React then unmounts the old HOC tree and mounts a fresh one,
  // resetting all category state and cascading effects on every keystroke.
  const Form = useMemo(() => WithCategorySelection(function DocumentFormInner({
    categoriesDropdown,
    subCategoriesDropdown,
  }) {
    const { values: formValues } = useFormikContext<IUploadDocument>();

    return (
      <UploadEntityFormSpacing>
        <Grid
          container
          spacing={3}
        >
          <Grid
            item
            xs={12}
          >
            <FastField
              label="Document Name"
              name={'name'}
              inputProps={{ maxLength: 100 }}
              component={TextInputField}
            />

            <GridDivider margin="20px 0 0" />
          </Grid>

          <Grid
            item
            xs={12}
          >
            <UploadEntityFormRow>
              <UploadEntityFormLabel>Category</UploadEntityFormLabel>

              <UploadEntityFormDropdownField>
                { categoriesDropdown }
              </UploadEntityFormDropdownField>

              <UploadEntityFormDropdownField>
                { subCategoriesDropdown }
              </UploadEntityFormDropdownField>
            </UploadEntityFormRow>

            <StyledDivider
              mt={20}
              mb={0}
            />
          </Grid>

          <Grid
            item
            xs={12}
          >
            <UploadEntityFormRow>
              <UploadEntityFormLabel>Assign</UploadEntityFormLabel>

              <FamilyMembersSelector
                hideAllSelection
                multiple
                name="assigned"
              />
            </UploadEntityFormRow>

            <StyledDivider
              mt={20}
              mb={0}
            />
          </Grid>

          <Grid
            item
            xs={12}
          >
            <UploadEntityFormRow>
              <UploadEntityFormLabel width="135px">Assignee Permissions</UploadEntityFormLabel>

              <UploadEntityFormDropdownField>
                <FastField
                  name={'assignedPermission'}
                  options={documentAccessLevels}
                  component={SelectField}
                />
              </UploadEntityFormDropdownField>
            </UploadEntityFormRow>

            <StyledDivider
              mt={20}
              mb={0}
            />
          </Grid>

          <Grid
            item
            xs={12}
          >
            <UploadEntityFormRow>
              <UploadEntityFormLabel>Tag <i>Optional</i></UploadEntityFormLabel>

              <FamilyMembersSelector
                hideAllSelection
                multiple
                name="tagged"
              />
            </UploadEntityFormRow>

            <StyledDivider
              mt={20}
              mb={0}
            />
          </Grid>

          <Grid
            item
            xs={12}
          >
            <DescriptionField
              multiline
              fullWidth
              rows={4}
              label={<>Description <i>Optional</i></>}
              name={'description'}
              inputProps={{ maxLength: 100 }}
              component={TextInputField}
            />
          </Grid>

          <GridDivider margin="-5px 0" />

          <Grid
            item
            xs={12}
          >
            <UploadEntityFormLabel>Attach File <i>Optional</i></UploadEntityFormLabel>

            <Box height={10} />

            <DocumentPicker
              multiple
              icon={<IconDocument />}
              files={filesRef.current as File[]}
              onChange={(f) => onChangeRef.current(f as File[])}
            />
          </Grid>

          <Grid
            item
            xs={12}
          >
            <ModalFooterActions
              formikRef={formikRef}
              disabled={loadingRef.current || !filesRef.current}
            >
              { loadingRef.current ? 'Uploading' : formValues?.id ? 'Edit' : 'Add' } Document
            </ModalFooterActions>
          </Grid>
        </Grid>
      </UploadEntityFormSpacing>
    );
  }), []);

  return useMemo(() => (
    <Form
      values={values}
      familyId={familyId}
      categories={categories}
      loading={loadingCategories}
      setFieldValue={setFieldValue}
    />
  ), [ loading, files, categories, values?.categoryId, values?.subCategoryId ]);
};
